/**
 * Hooks TanStack Query para intl_invoices.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import apiClient from '@/lib/apiClient'

import type {
  IntlInvoiceDetail,
  IntlInvoiceListItem,
  PostResult,
} from '../types'

// ─── Listado ──────────────────────────────────────────────────────────────────

interface ListFilters {
  status?: string
  supplier?: string
  date_from?: string
  date_to?: string
  search?: string
}

export function useIntlInvoices(filters: ListFilters = {}) {
  return useQuery({
    queryKey: ['intl-invoices', 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      for (const [k, v] of Object.entries(filters)) {
        if (v) params.set(k, v)
      }
      const qs = params.toString()
      const res = await apiClient.get<IntlInvoiceListItem[] | { results: IntlInvoiceListItem[] }>(
        `/intl-invoices/${qs ? `?${qs}` : ''}`,
      )
      return Array.isArray(res.data) ? res.data : res.data.results
    },
  })
}

// ─── Detalle ──────────────────────────────────────────────────────────────────

export function useIntlInvoice(id: string | null) {
  return useQuery({
    queryKey: ['intl-invoices', 'detail', id],
    queryFn: async () => {
      const res = await apiClient.get<IntlInvoiceDetail>(`/intl-invoices/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useUpdateIntlInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<IntlInvoiceDetail> }) => {
      const res = await apiClient.patch<IntlInvoiceDetail>(`/intl-invoices/${id}/`, data)
      return res.data
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['intl-invoices'] })
      qc.invalidateQueries({ queryKey: ['intl-invoices', 'detail', vars.id] })
    },
  })
}

export function usePostIntlInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, accountId, targetStatus, personalizados }: { id: string; accountId?: string; targetStatus?: 'approved' | 'registered'; personalizados?: any[] }) => {
      const body: Record<string, any> = {}
      if (accountId) body.account_id = accountId
      if (targetStatus) body.target_status = targetStatus
      if (personalizados && personalizados.length) body.personalizados = personalizados
      const res = await apiClient.post<PostResult>(`/intl-invoices/${id}/post/`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['intl-invoices'] })
    },
  })
}

export function useDiscardIntlInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`/intl-invoices/${id}/discard/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['intl-invoices'] }),
  })
}

export function useReparseIntlInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`/intl-invoices/${id}/reparse/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['intl-invoices'] }),
  })
}

export function useUploadIntlInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, supplier }: { file: File; supplier?: string }) => {
      const formData = new FormData()
      formData.append('file', file)
      if (supplier) formData.append('supplier', supplier)
      const res = await apiClient.post<IntlInvoiceDetail>('/intl-invoices/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['intl-invoices'] }),
  })
}

// ─── PDF URL ──────────────────────────────────────────────────────────────────

export function useIntlInvoicePdfUrl(id: string | null) {
  return useQuery({
    queryKey: ['intl-invoices', 'pdf', id],
    queryFn: async () => {
      const res = await apiClient.get<{ mode: 'local' | 's3'; url?: string; pdf_s3_key?: string }>(
        `/intl-invoices/${id}/pdf/`,
      )
      return res.data
    },
    enabled: !!id,
    staleTime: 60_000,
  })
}
